import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DESTRUCTIVE_OUTLINE, RelativeTime } from '../../../components/design-system/admin';
import { Thumb } from '../../../components/design-system/analytics/Thumb';
import { ScoreChip } from '../../../components/design-system/common';
import type { NetworkMatch } from '../../../common/hooks/use-network-api.hook';
import { formatLabel, methodLabel } from './network-labels';

/**
 * One question: is this product of yours that network product?
 *
 * The two side by side, picture first — a retailer recognises their own tile
 * faster than they read its name — and the evidence underneath as plain
 * chips: how sure overall, how it was found, how close the name and the look
 * are. The decision is two outlined buttons of equal weight; "not this one"
 * is not destructive, it is an answer.
 */
export const MatchCard: React.FC<{
    match: NetworkMatch;
    selected?: boolean;
    onSelect?: (selected: boolean) => void;
    busy?: boolean;
    onDecide: (status: 'confirmed' | 'rejected') => void;
}> = ({ match, selected, onSelect, busy, onDecide }) => {
    const { t } = useTranslation();
    const p = match.product;
    const np = match.networkProduct;

    return (
        <div className={cn('rounded-xl border bg-card p-4', selected && 'border-foreground/30')} data-testid="network-match" data-match-id={match.id}>
            <div className="flex items-start gap-3">
                {onSelect && (
                    <Checkbox
                        className="mt-6"
                        checked={!!selected}
                        onCheckedChange={(v) => onSelect(v === true)}
                        aria-label={t('NETWORK_RETAILER.MATCHES.SELECT_ONE', { name: p?.name || np?.name || '' })}
                    />
                )}
                <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
                    {p ? (
                        <Side
                            caption={t('NETWORK_RETAILER.MATCHES.YOUR_PRODUCT')}
                            image={p.image}
                            name={p.name}
                            href={`/products/${p.uuid}/edit`}
                            details={[p.sku, formatLabel(p.format), p.colour?.name, p.brand]}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground">{t('NETWORK_RETAILER.MATCHES.PRODUCT_GONE')}</p>
                    )}
                    <ArrowRight className="hidden size-4 text-muted-foreground sm:block rtl:-scale-x-100" aria-hidden />
                    {np ? (
                        <Side
                            caption={[np.brand?.name, np.collection?.name].filter(Boolean).join(' · ') || t('NETWORK_RETAILER.TITLE')}
                            image={np.image}
                            name={np.name}
                            href={np.brand ? `/products/network/brands/${np.brand.slug}` : undefined}
                            details={[np.ref, formatLabel(np.format), np.colour?.name, np.eans?.[0] ? `EAN ${np.eans[0]}` : undefined]}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground">{t('NETWORK_RETAILER.MATCHES.NETWORK_GONE')}</p>
                    )}
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                <div className="flex flex-wrap items-center gap-1.5">
                    <ScoreChip label={t('NETWORK_RETAILER.MATCHES.SCORE')} value={match.score} emphasis testId="network-match-score" />
                    <ScoreChip label={methodLabel(t, match.method)} />
                    {typeof match.evidence?.nameScore === 'number' && <ScoreChip label={t('NETWORK_RETAILER.MATCHES.NAME')} value={match.evidence.nameScore} />}
                    {typeof match.evidence?.visualScore === 'number' && <ScoreChip label={t('NETWORK_RETAILER.MATCHES.LOOK')} value={match.evidence.visualScore} />}
                    {/* A barcode found by another method is still worth saying; found BY barcode, the method chip already says it. */}
                    {match.evidence?.ean && match.method !== 'ean' && <ScoreChip label={t('NETWORK_RETAILER.MATCHES.SAME_EAN')} />}
                    {match.alternatives > 0 && (
                        <span className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.MATCHES.ALTERNATIVES', { count: match.alternatives })}</span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {match.status !== 'suggested' && (
                        <span className="text-xs text-muted-foreground">
                            {t(match.status === 'confirmed' ? 'NETWORK_RETAILER.MATCHES.CONFIRMED_BY' : 'NETWORK_RETAILER.MATCHES.REJECTED_BY', {
                                who: match.decidedBy === 'auto' ? t('NETWORK_RETAILER.MATCHES.AUTO') : match.decidedBy || '—',
                            })}{' '}
                            {match.decidedAt && <RelativeTime value={match.decidedAt} />}
                        </span>
                    )}
                    {match.status !== 'rejected' && (
                        <Button
                            variant="outline"
                            size="sm"
                            className={match.status === 'confirmed' ? DESTRUCTIVE_OUTLINE : undefined}
                            disabled={busy}
                            onClick={() => onDecide('rejected')}
                            data-testid="network-match-reject"
                        >
                            <X data-icon="inline-start" />
                            {match.status === 'confirmed' ? t('NETWORK_RETAILER.MATCHES.UNLINK') : t('NETWORK_RETAILER.MATCHES.REJECT')}
                        </Button>
                    )}
                    {match.status !== 'confirmed' && (
                        <Button variant="outline" size="sm" disabled={busy || !p} onClick={() => onDecide('confirmed')} data-testid="network-match-confirm">
                            {busy ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Check data-icon="inline-start" />}
                            {match.status === 'rejected' ? t('NETWORK_RETAILER.MATCHES.CONFIRM_AFTER_ALL') : t('NETWORK_RETAILER.MATCHES.CONFIRM')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const Side: React.FC<{ caption: string; image?: string; name: string; href?: string; details: Array<string | undefined> }> = ({
    caption,
    image,
    name,
    href,
    details,
}) => (
    <div className="flex min-w-0 items-center gap-3">
        <Thumb src={image} alt={name} size="fill" className="size-16 rounded-lg" />
        <div className="min-w-0">
            <p className="truncate text-xs text-muted-foreground">{caption}</p>
            {href ? (
                <Link to={href} className="block truncate text-sm font-medium hover:underline">
                    {name}
                </Link>
            ) : (
                <p className="truncate text-sm font-medium">{name}</p>
            )}
            <p className="truncate text-xs text-muted-foreground">{details.filter(Boolean).join(' · ')}</p>
        </div>
    </div>
);
